import { Grade } from './types';

export const SCORE_MAP_7_SCALE: Record<string, number> = {
  '5**': 7,
  '5*': 6,
  '5': 5,
  '4': 4,
  '3': 3,
  '2': 2,
  '1': 1,
  'U': 0,
  // Category B - Applied Learning grades
  'Dist II': 4,   // Attained with Distinction (II) - comparable to Level 4
  'Dist I': 3,    // Attained with Distinction (I) - comparable to Level 3
  'Att': 2,       // Attained - comparable to Level 2
  'Unatt': 0,     // Unattained - below Level 2
  // Category C - Urdu (traditional letter grades)
  'A': 5, // Also used for CSD: Attained
  'B': 4,
  'C': 3,
  'D': 2,
  'E': 1,
  // Category C - French/German/Spanish (CEFR levels)
  'C2': 7,  // Highest proficiency
  'C1': 5.5,
  'B2': 4,
  'B1': 3,
  'A2': 3,  // Minimum accepted level
  // Category C - Japanese (JLPT levels)
  'N1': 7,  // Highest proficiency
  'N2': 5.5,
  'N3': 4,
  // Category C - Korean (TOPIK grades)
  'Grade 6': 7,  // Grade 6 (highest)
  'Grade 5': 5.5,
  'Grade 4': 4,
  'Grade 3': 3,
  'N/A': 0
};

export const SCORE_MAP_8_5_SCALE: Record<string, number> = {
  '5**': 8.5,
  '5*': 7,
  '5': 5.5,
  '4': 4,
  '3': 3,
  '2': 2,
  '1': 1,
  'U': 0,
  // Category B - Applied Learning grades
  'Dist II': 4,   // Attained with Distinction (II) - comparable to Level 4
  'Dist I': 3,    // Attained with Distinction (I) - comparable to Level 3
  'Att': 2,       // Attained - comparable to Level 2
  'Unatt': 0,     // Unattained - below Level 2
  // Category C - Urdu (traditional letter grades)
  'A': 7,   // Also used for CSD: Attained
  'B': 5.5,
  'C': 4,
  'D': 2.5,
  'E': 1,
  // Category C - French/German/Spanish (CEFR levels)
  'C2': 7,   // Highest proficiency (equivalent to 5*)
  'C1': 5.5, // (equivalent to 5)
  'B2': 4,
  'B1': 3,
  'A2': 3,   // Minimum accepted level
  // Category C - Japanese (JLPT levels)
  'N1': 7,   // Highest proficiency
  'N2': 5.5,
  'N3': 4,
  // Category C - Korean (TOPIK grades)
  'Grade 6': 7,   // Grade 6 (highest)
  'Grade 5': 5.5,
  'Grade 4': 4,
  'Grade 3': 3,
  'N/A': 0
};

// Imports from shared data
import subjectsDataRaw from '../../data/subjects.json';
// We might not need subject_categories.json if subjects.json has everything, 
// but importing it as requested to be safe or if needed for specific logic.
// Actually subjects.json has the type/category info, so we can rely on that.

// Define the shape of the subject data (optional but good for TS)
interface SubjectData {
  name_eng: string;
  name_tch: string;
  type: string;
  strand?: string;  // For subjects like BAFS (Accounting) that belong to a strand
  remarks?: string;
  "cutoff-marks"?: { [year: string]: { [grade: string]: number | string } };
  "cutoff-marks-by-paper"?: {
    [paperName: string]: { [year: string]: { [grade: string]: number | string } };
  };
}

const subjectsData = subjectsDataRaw as SubjectData[];

// Export subjects data for use in calculator
export const SUBJECTS_DATA = subjectsData;

// Create a lookup map for subject strand information
export const SUBJECT_STRAND_MAP: { [subject: string]: string } = {};
subjectsData.forEach(subject => {
  if (subject.strand) {
    SUBJECT_STRAND_MAP[subject.name_eng] = subject.strand;
  }
});

// Category A - Core subjects
export const CATEGORY_A_CORE = subjectsData
  .filter(s => s.type === 'core')
  .map(s => s.name_eng);

// Category A - Elective subjects
export const CATEGORY_A_ELECTIVE = subjectsData
  .filter(s => s.type === 'A')
  .map(s => s.name_eng);

// Category B - Applied Learning subjects
export const CATEGORY_B_SUBJECTS = subjectsData
  .filter(s => s.type === 'B')
  .map(s => s.name_eng);

// Category C - Other Languages
export const CATEGORY_C_SUBJECTS = subjectsData
  .filter(s => s.type === 'C')
  .map(s => s.name_eng);

// All subjects combined
export const SUBJECT_LIST = [
  ...CATEGORY_A_CORE,
  ...CATEGORY_A_ELECTIVE,
  ...CATEGORY_B_SUBJECTS,
  ...CATEGORY_C_SUBJECTS,
];

// Subjects by category for filtering
export const SUBJECTS_BY_CATEGORY: Record<'All' | 'A' | 'B' | 'C', string[]> = {
  'All': SUBJECT_LIST,
  'A': [...CATEGORY_A_CORE, ...CATEGORY_A_ELECTIVE],
  'B': CATEGORY_B_SUBJECTS,
  'C': CATEGORY_C_SUBJECTS,
};


// Standard grades for Category A subjects
export const GRADES_LIST: Grade[] = ['5**', '5*', '5', '4', '3', '2', '1', 'N/A', 'U'];

// Category B - Applied Learning grades
export const CATEGORY_B_GRADES_LIST: Grade[] = ['Dist II', 'Dist I', 'Att', 'Unatt', 'N/A'];

// Letter grades for Category C (Other Languages) subjects - language specific
export const CATEGORY_C_GRADES_LIST: Grade[] = ['A++', 'A+', 'A', 'B++', 'B+', 'B', 'C', 'D', 'E', 'N/A']; // Urdu
export const CATEGORY_C_CEFR_GRADES_LIST: Grade[] = ['C2', 'C1', 'B2', 'B1', 'A2', 'N/A']; // French, German, Spanish
export const CATEGORY_C_JAPANESE_GRADES_LIST: Grade[] = ['N1', 'N2', 'N3', 'N/A']; // Japanese
export const CATEGORY_C_KOREAN_GRADES_LIST: Grade[] = ['Grade 6', 'Grade 5', 'Grade 4', 'Grade 3', 'N/A']; // Korean